import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import clsx from "clsx";
import { makeStyles, lighten } from "@material-ui/core/styles";

import { Link as ReactRouterLink, useLocation } from "react-router-dom";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";

import { userGetInventoryTypeSelector } from "src/store/system/selector";
import type { Theme } from "src/theme";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import { getTourStepType } from "src/store/tour/selector";
import { recordUserActivated } from "src/apiService/modules/users";
import { getUploads } from "src/store/uploads/selector";

let firedThisSession = false;

const useStyles = makeStyles((theme: Theme) => {
  const { custom: palette, derived } = theme.palette.custom;
  const brandSecondary = palette.brandSecondary;

  return {
    root: {
      "& > * + *": {
        marginLeft: theme.spacing(2),
      },
      display: "flex",
      margin: theme.spacing(2, 0, 2, 0),
    },
    button: {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: derived.shadow.xs,
      transition:
        "transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
        boxShadow: derived.shadow.sm,
        transform: "translateY(-2px)",
      },
    },
    active: {
      "&:hover": {
        backgroundColor: lighten(brandSecondary, 0.2),
        boxShadow: derived.shadow.sm,
        transform: "translateY(-2px)",
      },
      color: theme.palette.getContrastText(brandSecondary),
      backgroundColor: brandSecondary,
    },
    disabled: {
      color: `${theme.palette.action.disabled} !important`,
      backgroundColor: `${theme.palette.background.paper} !important`,
    },
  };
});

export const Entries = [
  {
    to: "/transaction-details/all",
    text: "All",
  },
  {
    to: "/transaction-details/inventory",
    text: "Inventory",
    inventoryType: "accrual",
    disabledTooltip:
      "Inventory is disabled currently because you are set to the cash inventory method. To view the inventory features, click the gear icon and change to the accrual inventory method.",
  },
  { to: "/transaction-details/sales", text: "Sales" },
  { to: "/transaction-details/refunds", text: "Refunds" },
  { to: "/transaction-details/expenses", text: "Expenses" },
  { to: "/transaction-details/other", text: "Other" },
];

function Menu() {
  const recordEvent = useRecordEvent();

  const classes = useStyles();
  const location = useLocation();
  const inventoryType = useSelector(userGetInventoryTypeSelector);
  const tourStep = useSelector(getTourStepType);
  const uploads = useSelector(getUploads);

  useEffect(() => {
    if (firedThisSession) return;
    const hasSalesUpload = uploads.some((u) => u.type === "sales" && u.n > 0);
    if (hasSalesUpload) {
      firedThisSession = true;
      recordUserActivated();
    }
  }, [uploads]);

  return (
    <div className={classes.root} id={"transaction-details-page-buttons"}>
      {Entries.map((entry) => {
        const disabled =
          entry.inventoryType && entry.inventoryType !== inventoryType;
        const active = location.pathname === entry.to;
        const to =
          tourStep === "viewing_data" &&
          entry.to === "/transaction-details/expenses"
            ? "/transaction-details/expenses?view=all"
            : entry.to;
        const button = (
          <Button
            disabled={disabled || false}
            key={entry.to}
            variant="contained"
            size="large"
            component={ReactRouterLink}
            to={to}
            className={clsx(
              classes.button,
              active && classes.active,
              disabled && classes.disabled
            )}
            onClick={() => {
              recordEvent({
                type: "feature-adoption",
                feature: "transaction-details",
                element: entry.text.toLowerCase(),
              });
            }}
          >
            {entry.text}
          </Button>
        );

        if (disabled && entry.disabledTooltip)
          return (
            <Tooltip key={entry.to} title={entry.disabledTooltip}>
              <span>{button}</span>
            </Tooltip>
          );
        return button;
      })}
    </div>
  );
}

export default Menu;
